import { Component, inject, OnInit, signal, HostListener } from '@angular/core';
import {
  FormControl,
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { AuthService } from '@app/auth/auth.service';
import { TextInputDialogComponent } from '@app/shared/text-input-dialog/text-input-dialog.component';
import { InfoDialogComponent } from '@app/shared/info-dialog/info-dialog.component';
import { AppService } from '@app/app.service';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatSelectModule } from '@angular/material/select';

@Component({
  selector: 'app-user-settings',
  templateUrl: './user-settings.component.html',
  styleUrl: './user-settings.component.scss',
  standalone: true,
  imports: [
    MatInputModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatTooltipModule,
    MatDialogModule,
    MatIconModule,
    MatDividerModule,
    MatSelectModule,
  ],
})
export class UserSettingsComponent implements OnInit {
  authService = inject(AuthService);
  dialog = inject(MatDialog);
  appService = inject(AppService);
  loading = signal(false);

  apiKeys = signal<any[]>([]);

  form: FormGroup;

  apiTodayUsage = signal(0);
  apiDailyLimit = signal(0);

  isMobile = window.innerWidth < 768;

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < 768;
  }

  carriers: any[] = [];
  brokers: any[] = [];

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      default_search_radius_miles: new FormControl('', [
        Validators.required,
        Validators.min(50),
        Validators.max(500),
      ]),
      default_carrier_id: new FormControl<number | null>(null),
      default_broker_id: new FormControl<number | null>(null),
    });
  }

  ngOnInit(): void {
    this.getApiKeys();
    this.getUserSettings();
    this.getUserApiUsage();
    this.getUserConnections();
  }

  getUserApiUsage(): void {
    this.authService.getApiUsage().subscribe({
      next: (response: any) => {
        this.apiTodayUsage.set(response.data?.today_api_usage ?? 0);
        this.apiDailyLimit.set(response.data?.api_daily_limit ?? 0);
      },
      error: (error: any) => {
        this.appService.showSnackBar(error.error.message, 'error');
        this.apiTodayUsage.set(0);
        this.apiDailyLimit.set(0);
      },
    });
  }

  getUserSettings(): void {
    this.authService.getUserSettings().subscribe({
      next: (response: any) => {
        this.authService.setUserSettingsToStorage(response.data);
        this.form.patchValue(response.data);
      },
      error: (error: any) => {
        this.appService.showSnackBar(error.error.message, 'error');
      },
    });
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.loading.set(true);

    this.authService.updateUserSettings(this.form.value).subscribe({
      next: (response: any) => {
        this.authService.setUserSettingsToStorage(response.data);
        this.loading.set(false);
        this.appService.showSnackBar(response.message, 'success');
      },
      error: (error: any) => {
        this.appService.showSnackBar(error.error.message, 'error');
        this.loading.set(false);
      },
    });
  }

  getApiKeys(): void {
    this.loading.set(true);

    this.authService.getApiKeys().subscribe({
      next: (response: any) => {
        this.apiKeys.set(response.data ?? []);
        this.loading.set(false);
      },
      error: (error: any) => {
        this.appService.showSnackBar(error.error.message, 'error');
        this.loading.set(false);
      },
    });
  }

  openCreateApiKeyDialog(): void {
    const dialogRef = this.dialog.open(TextInputDialogComponent, {
      width: '400px',
      data: {
        title: 'Create API Key',
        label: 'API Key Name',
        placeholder: 'Example: My TMS',
        buttonLabel: 'Create',
        required: true,
      },
    });

    dialogRef.afterClosed().subscribe((name: string | undefined) => {
      if (!name) {
        return;
      }

      this.createApiKey(name);
    });
  }

  createApiKey(name: string): void {
    this.loading.set(true);

    this.authService.createApiKey({ name }).subscribe({
      next: (response: any) => {
        const apiKey = response.data?.api_key;

        this.getApiKeys();

        if (apiKey) {
          this.dialog.open(InfoDialogComponent, {
            width: '400px',
            data: {
              message: 'Copy this API key now. It will not be shown again.',
              message_warning: true,
              copyText: apiKey,
            },
          });
        }
      },
      error: (error: any) => {
        this.appService.showSnackBar(error.error.message, 'error');
        this.loading.set(false);
      },
    });
  }

  deleteApiKey(apiKey: any): void {
    const confirmMessage = `Are you sure you want to delete the API key "${apiKey.name}"? You will lose access to any application using this API key.`;

    this.appService.confirm(confirmMessage).subscribe((result) => {
      if (result) {
        this.loading.set(true);

        this.authService.deleteApiKey(apiKey.id).subscribe({
          next: (res: any) => {
            this.appService.showSnackBar(res.message, 'success');
            this.getApiKeys();
          },
          error: (error: any) => {
            this.appService.showSnackBar(error.error.message, 'error');
            this.loading.set(false);
          },
        });
      }
    });
  }

  refreshApiKeys(): void {
    this.getUserApiUsage();
    this.getApiKeys();
  }

  openApiDocumentation(): void {
    window.open('/pages/api-documentation', '_blank');
  }

  getUserConnections(): void {
    this.authService.getUserConnections().subscribe({
      next: (response: any) => {
        this.populateCarrierLists(response.data ?? []);
      },
      error: (error: any) => {
        this.appService.showSnackBar(error.error.message, 'error');
      },
    });
  }

  private populateCarrierLists(connections: any[]): void {
    this.carriers = [];
    this.brokers = [];

    for (const connection of connections) {
      const carrier = connection.carrier;

      if (!carrier) {
        continue;
      }

      const option = {
        id: carrier.id,
        name: carrier.dba_name?.trim() || carrier.legal_name,
        dot_number: carrier.dot_number,
      };

      if (carrier.is_carrier) {
        this.carriers.push(option);
      }

      if (carrier.is_broker) {
        this.brokers.push(option);
      }
    }
  }
}
